import { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Send, Trash2, Mail, Monitor, AtSign } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Highlight @mentions in comment text
const renderCommentBody = (text) => {
  const parts = text.split(/(@[A-Z][a-z]+(?:\s[A-Z][a-z]+){0,2})/g);
  return parts.map((part, i) => {
    if (part.startsWith('@') && /^@[A-Z]/.test(part)) {
      return (
        <span key={i} className="px-1 py-0.5 rounded bg-indigo-500/20 text-indigo-400 font-medium text-xs">
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const CommentsSection = ({ itemType, itemId, token }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Mention autocomplete state
  const [teamMembers, setTeamMembers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const inputRef = useRef(null);
  const mentionRef = useRef(null);

  // Fetch team members for autocomplete
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/auth/team-members`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setTeamMembers(d.members || []))
      .catch(() => {});
  }, [token]);

  const fetchComments = useCallback(async () => {
    if (!itemId || !token) return;
    try {
      const res = await fetch(`${API_URL}/api/dashboard/comments/${itemType}/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (e) {
      console.error('Failed to fetch comments:', e);
    } finally {
      setLoading(false);
    }
  }, [itemType, itemId, token]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = newComment.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setShowMentions(false);
    try {
      const res = await fetch(`${API_URL}/api/dashboard/comments/${itemType}/${itemId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ body: text }),
      });
      if (res.ok) {
        const comment = await res.json();
        setComments(prev => [...prev, comment]);
        setNewComment('');
      }
    } catch (e) {
      console.error('Failed to post comment:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      const res = await fetch(`${API_URL}/api/dashboard/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setComments(prev => prev.filter(c => c.id !== commentId));
      }
    } catch (e) {
      console.error('Failed to delete comment:', e);
    }
  };

  // Handle input changes for @mention detection
  const handleInputChange = (e) => {
    const val = e.target.value;
    setNewComment(val);

    // Check if we're in a @mention context
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);

    if (atMatch) {
      setMentionQuery(atMatch[1].toLowerCase());
      setShowMentions(true);
      setMentionIndex(0);
    } else {
      setShowMentions(false);
    }
  };

  // Handle keyboard navigation in mention dropdown
  const handleKeyDown = (e) => {
    if (!showMentions || filteredMembers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex(i => Math.min(i + 1, filteredMembers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && showMentions) {
      e.preventDefault();
      insertMention(filteredMembers[mentionIndex]);
    } else if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  const insertMention = (member) => {
    const cursorPos = inputRef.current?.selectionStart || newComment.length;
    const textBeforeCursor = newComment.slice(0, cursorPos);
    const textAfterCursor = newComment.slice(cursorPos);
    const atStart = textBeforeCursor.lastIndexOf('@');

    if (atStart >= 0) {
      const before = textBeforeCursor.slice(0, atStart);
      const mention = `@${member.name} `;
      setNewComment(before + mention + textAfterCursor);
      setShowMentions(false);

      // Restore focus
      setTimeout(() => {
        if (inputRef.current) {
          const pos = before.length + mention.length;
          inputRef.current.focus();
          inputRef.current.setSelectionRange(pos, pos);
        }
      }, 0);
    }
  };

  const filteredMembers = teamMembers.filter(m =>
    (m.name || '').toLowerCase().includes(mentionQuery)
  ).slice(0, 6);

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="mt-5 border-t border-white/10 pt-4" data-testid="comments-section">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-300">
          Comments {comments.length > 0 && <span className="text-gray-500">({comments.length})</span>}
        </span>
      </div>

      {loading ? (
        <div className="text-xs text-gray-500 py-2">Loading...</div>
      ) : (
        <div className="space-y-3 max-h-48 overflow-y-auto mb-3">
          <AnimatePresence>
            {comments.map((c) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="group p-3 rounded-xl bg-white/5 border border-white/5"
                data-testid={`comment-${c.id}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-indigo-400">
                        {c.authorName?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-gray-300">{c.authorName}</span>
                    {c.source === 'email_reply' && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 text-[10px]">
                        <Mail className="w-2.5 h-2.5" /> email
                      </span>
                    )}
                    {c.source === 'ui' && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-500/10 text-gray-500 text-[10px]">
                        <Monitor className="w-2.5 h-2.5" /> app
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-600">{timeAgo(c.createdAt)}</span>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/10 transition-all"
                      data-testid={`delete-comment-${c.id}`}
                    >
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">
                  {renderCommentBody(c.body)}
                </p>
              </motion.div>
            ))}
          </AnimatePresence>
          {comments.length === 0 && (
            <p className="text-xs text-gray-600 py-2">No comments yet. Be the first to add one.</p>
          )}
        </div>
      )}

      <div className="relative" ref={mentionRef}>
        <form onSubmit={handleSubmit} className="flex gap-2" data-testid="comment-form">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              value={newComment}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Add a comment... (use @ to mention)"
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500/50"
              data-testid="comment-input"
            />
            {/* @Mention Autocomplete Dropdown */}
            <AnimatePresence>
              {showMentions && filteredMembers.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  className="absolute bottom-full mb-1 left-0 w-full rounded-xl bg-[#0d1221] border border-white/10 shadow-2xl overflow-hidden z-50"
                  data-testid="mention-dropdown"
                >
                  <div className="px-3 py-1.5 border-b border-white/5 flex items-center gap-1.5">
                    <AtSign className="w-3 h-3 text-indigo-400" />
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Mention someone</span>
                  </div>
                  {filteredMembers.map((m, i) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => insertMention(m)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                        i === mentionIndex ? 'bg-indigo-500/15' : 'hover:bg-white/5'
                      }`}
                      data-testid={`mention-option-${m.id}`}
                    >
                      <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-[9px] font-bold text-indigo-400">{m.initials}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{m.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">{m.email}</p>
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            className="px-3 py-2 rounded-xl bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="comment-submit-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default CommentsSection;
